// =================================================================
// Sessions library
//
// Two things this file does:
//   1. Provides helpers for starting and completing a game session.
//      "Start" writes a session record to Firebase and returns the
//      session key. "Complete" stamps completed_at on a previously
//      started session.
//   2. Computes engagement metrics from a sessions snapshot — the
//      weekly play count and the consecutive-day streak.
//
// The Brain Health side initiates a session before redirecting the
// resident to games.quiltt.com. The games site, on game completion,
// redirects the resident back with `?completed=<sessionKey>` in the
// URL. The dashboard detects that param and completes the session
// on this side.
//
// Identity-source-agnostic API (Phase 2C):
//   startSession and completeSession accept EITHER `accountId`
//   (legacy, writes to /brain_health/accounts/...) OR `basePath`
//   (new, writes wherever you point it). Pass `basePath` when the
//   caller may be an individual subscriber or a device, both of
//   which live outside /accounts/. `basePath` wins if both are
//   provided. Existing community-resident callers passing
//   `accountId` continue to work unchanged.
// =================================================================

import { ref, push, set, update } from 'firebase/database';
import { db } from './firebase.js';
import { domainForGoal, getGame } from '../data/gamesCatalog.js';

/**
 * Resolve the RTDB base path for this session call.
 * Either explicit `basePath` (preferred for new code), or a legacy
 * `accountId` that we expand to the historical /accounts/ path.
 */
function resolveBasePath({ accountId, basePath }) {
  if (basePath) return basePath;
  if (accountId) return `brain_health/accounts/${accountId}`;
  throw new Error('sessions: must provide basePath or accountId');
}

/**
 * Start a session record for the resident. Writes to RTDB, returns
 * the session key (used as the `?completed=` token on return).
 *
 * @param {object} args
 *   @param {string} [args.basePath]   RTDB path to the identity record
 *                                     (e.g. 'brain_health/users/xyz')
 *   @param {string} [args.accountId]  legacy — uses /brain_health/accounts/{accountId}
 *   @param {object} args.game         catalog game object
 *   @param {string} args.mode         mode string for the level
 *   @param {string} args.level        resident's level at start
 * @returns {Promise<string>} the new session's RTDB key
 */
export async function startSession({ accountId, basePath, game, mode, level }) {
  const path = resolveBasePath({ accountId, basePath });

  const sessionsRef = ref(db, `${path}/sessions`);
  const newRef = push(sessionsRef);
  const sessionKey = newRef.key;

  // Derive the session's domain tags from the game's GOALS list (the
  // new model). game.goals[0] is the primary goal; the rest are
  // secondary. Each goal maps to a cognitive domain, which is what we
  // record. (The old model stored game.domains.{primary,secondary},
  // which no longer exists — reading it produced `undefined`, and
  // Firebase set() rejects any record containing undefined.)
  const goals = Array.isArray(game.goals) ? game.goals : [];
  const primaryDomain = goals[0] ? domainForGoal(goals[0]) : null;
  const secondaryDomain = goals[1] ? domainForGoal(goals[1]) : null;

  // Build the record incrementally so we NEVER assign undefined to a
  // property. Only fields with real values are included; a missing
  // domain is simply omitted, not written as undefined.
  const record = {
    game_id: game.id,
    started_at: Date.now(),
    source: 'dashboard',
  };
  if (primaryDomain?.key) record.domain_primary = primaryDomain.key;
  if (secondaryDomain?.key) record.domain_secondary = secondaryDomain.key;
  // Record the matched goal keys (e.g. ['A','D']) for analytics. The
  // session rules validate each element is a goal key A-E. Only write
  // if present so we never set undefined.
  if (goals.length > 0) record.goals = goals;

  await set(newRef, record);

  // Best-effort: update last_active so the admin sees freshness.
  // Not awaited — fire and forget; if it fails it's not user-visible.
  set(
    ref(db, `${path}/last_active`),
    Date.now()
  ).catch(() => {});

  return sessionKey;
}

/**
 * Mark a session complete. Idempotent: writing completed_at twice
 * is harmless (last value wins, which is fine for our usage).
 */
export async function completeSession({ accountId, basePath, sessionKey }) {
  const path = resolveBasePath({ accountId, basePath });
  await update(
    ref(db, `${path}/sessions/${sessionKey}`),
    { completed_at: Date.now() }
  );
}

// -----------------------------------------------------------------
// Engagement metrics
// -----------------------------------------------------------------

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/**
 * Snapshot of sessions for the resident. Treats a session as
 * "played" if it has a started_at (we count attempts, not just
 * completions — partial sessions still represent engagement).
 *
 * This function is identity-source-agnostic: it operates on the
 * sessions map directly. Pass account.sessions for community
 * residents or userRecord.sessions for individuals — same shape.
 *
 * @param {object} sessionsMap  raw RTDB sessions object, or null
 * @param {Date} now            defaults to new Date()
 * @returns {{ weekCount, streak, totalPlays }}
 */
export function engagementSummary(sessionsMap, now = new Date()) {
  const sessions = sessionsMap
    ? Object.values(sessionsMap).filter((s) => s && s.started_at)
    : [];

  if (sessions.length === 0) {
    return { weekCount: 0, streak: 0, totalPlays: 0 };
  }

  // Sessions in the last 7 days
  const sevenDaysAgo = now.getTime() - 7 * MS_PER_DAY;
  const weekCount = sessions.filter((s) => s.started_at >= sevenDaysAgo).length;

  // Streak: count consecutive days going backward from today that
  // have at least one session. If today has no session but yesterday
  // did, the streak from yesterday is still counted (we don't want
  // a streak to "die" mid-day).
  const dayKeys = new Set(
    sessions.map((s) => dateKey(new Date(s.started_at)))
  );
  let streak = 0;
  let cursor = new Date(now);
  // Walk backward day by day. Stop on the first miss UNLESS we're on
  // today and today is empty — then check yesterday before stopping.
  let allowedMisses = dayKeys.has(dateKey(cursor)) ? 0 : 1;
  while (true) {
    const key = dateKey(cursor);
    if (dayKeys.has(key)) {
      streak += 1;
      cursor.setDate(cursor.getDate() - 1);
    } else if (allowedMisses > 0) {
      allowedMisses -= 1;
      cursor.setDate(cursor.getDate() - 1);
    } else {
      break;
    }
    // Defensive: don't walk back more than 90 days even if data
    // somehow extends that far without breaks.
    if (streak > 90) break;
  }

  return {
    weekCount,
    streak,
    totalPlays: sessions.length,
  };
}

function dateKey(d) {
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
}

/**
 * Per-goal activity over a trailing window (default 30 days).
 *
 * IMPORTANT — why this reports *estimated* minutes, not measured time:
 *   A session stores `started_at` on launch and `completed_at` only if
 *   the resident returns to the dashboard via `?completed=`. Many
 *   sessions never complete (tab closed, navigated away on the games
 *   site), so `completed_at - started_at` is missing for a large share
 *   of plays — and even when present it's wall-clock time on the games
 *   site, not focused play. Reporting that as "minutes played" would be
 *   misleading and frequently zero.
 *
 *   Instead we attribute each session the game's catalog
 *   `estimated_minutes` and roll it up by GOAL. This is an honest,
 *   stable signal of how a resident's effort is distributed across
 *   their goals — which is the question the dashboard is actually
 *   answering — without claiming precision we don't have.
 *
 * Attribution: each session is attributed to a single goal — its
 * PRIMARY goal (session.goals[0], i.e. the game's first declared
 * goal). Using only the primary avoids double-counting a single play
 * across multiple goals, so the per-goal minutes sum to the total.
 *
 * Sessions whose game is no longer in the catalog, or that carry no
 * goal tag (older records written before the goals model), are still
 * counted in `totalMinutes`/`totalPlays` but land in `unattributed`
 * so the totals stay honest.
 *
 * @param {object} sessionsMap  raw RTDB sessions object, or null
 * @param {object} [opts]
 *   @param {number} [opts.windowDays=30]  trailing window length
 *   @param {Date}   [opts.now=new Date()]
 * @returns {{
 *   windowDays: number,
 *   byGoal: Object<string, { plays: number, minutes: number }>,
 *   totalPlays: number,
 *   totalMinutes: number,
 *   unattributed: { plays: number, minutes: number },
 * }}
 *   `byGoal` is keyed by goal key (A-E). Only goals with at least one
 *   play in the window appear.
 */
export function goalActivitySummary(sessionsMap, opts = {}) {
  const windowDays = opts.windowDays ?? 30;
  const now = opts.now ?? new Date();
  const cutoff = now.getTime() - windowDays * MS_PER_DAY;

  const result = {
    windowDays,
    byGoal: {},
    totalPlays: 0,
    totalMinutes: 0,
    unattributed: { plays: 0, minutes: 0 },
  };

  if (!sessionsMap) return result;

  for (const s of Object.values(sessionsMap)) {
    if (!s || !s.started_at || s.started_at < cutoff) continue;

    const game = getGame(s.game_id);
    // Fall back to 0 minutes (not undefined) if the game is unknown —
    // the play still counts, it just adds no minutes.
    const minutes = game?.estimated_minutes ?? 0;

    result.totalPlays += 1;
    result.totalMinutes += minutes;

    // Primary goal: prefer the session's own recorded goals (written
    // at start), fall back to the live catalog if an older session
    // lacks them. May still be missing for very old records.
    const primaryGoal =
      (Array.isArray(s.goals) && s.goals[0]) ||
      (Array.isArray(game?.goals) && game.goals[0]) ||
      null;

    if (!primaryGoal) {
      result.unattributed.plays += 1;
      result.unattributed.minutes += minutes;
      continue;
    }

    if (!result.byGoal[primaryGoal]) {
      result.byGoal[primaryGoal] = { plays: 0, minutes: 0 };
    }
    result.byGoal[primaryGoal].plays += 1;
    result.byGoal[primaryGoal].minutes += minutes;
  }

  return result;
}
